import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import {
  CalendarOutlined,
  DownOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Spin } from "antd";
import moment from "moment";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import DatePicker from "../../../../Common/DatePicker/DatePicker";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import TitledPanel from "../TitledPanel";
import UsageInsightCards from "../UsageInsightCards";

import {
  getDefaultUsageTrendDateRange,
  mapCreditConsumptionGraphByLocation,
  mapCreditConsumptionGraphToTrend,
  mapCreditConsumptionInsights,
} from "../../utils";

import { fetchCreditConsumptionGraph, getDefaultCreditsDateRange } from "../../../../../actions/usage";

import { designTokens } from "../../../../../styles/tokens";

import "./TestUsageTrend.css";

const GROUP_BY_OPTIONS = [
  { key: "location", label: "By Location" },
  { key: "category", label: "By category" },
];

const getOneMonthTrendRange = () => {
  const [startDate, endDate] = getDefaultCreditsDateRange().split(",");
  return { startDate, endDate };
};

const formatTrendPeriodLabel = (startDate, endDate) => (
  `${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format("MMM D, YYYY")}`
);

const SERIES_COLORS = {
  colorAnalyticsPurple: designTokens.colorAnalyticsPurple,
  colorAnalyticsRust: designTokens.colorAnalyticsRust,
  colorAnalyticsTealGreen: designTokens.colorAnalyticsTealGreen,
  colorAnalyticsPink: designTokens.colorAnalyticsPink,
  colorAnalyticsBlue: designTokens.colorAnalyticsBlue,
  colorAnalyticsSage: designTokens.colorAnalyticsSage,
};

/**
 * Builds CSV headers/rows from the chart series currently shown in the trend panel.
 * @param {Array} data
 * @param {Array} series
 * @returns {{ headers: Array<{ label: string, key: string }>, rows: Array }}
 */
const buildUsageTrendCsvExport = (data, series) => {
  const headers = [
    { label: "Week", key: "week" },
    ...series.map((item) => ({
      label: item.label,
      key: item.key,
    })),
  ];

  const rows = data.map((point) => {
    const row = { week: point.week };
    series.forEach((item) => {
      row[item.key] = point[item.key];
    });
    return row;
  });

  return { headers, rows };
};

const buildUsageTrendCsvFilename = ({
  groupBy,
  location,
  startDate,
  endDate,
}) => {
  const groupLabel = groupBy === "location" ? "By_Location" : "By_Category";
  const locationPart = String(location).trim()
    ? `_${String(location).trim().replace(/\s+/g, "_")}`
    : "";
  const rangePart = startDate && endDate
    ? `_${startDate}_to_${endDate}`
    : "";
  return `Test_Usage_Trend_${groupLabel}${locationPart}${rangePart}.csv`;
};

const getChartYDomain = (data = [], series = []) => {
  let maxValue = 0;
  data.forEach((point) => {
    series.forEach((item) => {
      const value = Number(point[item.key]);
      if (value > maxValue) maxValue = value;
    });
  });

  if (maxValue <= 0) return { domain: [0, 4], ticks: [0, 1, 2, 3, 4] };

  let step = 100;
  if (maxValue <= 5) step = 1;
  else if (maxValue <= 10) step = 2;
  else if (maxValue <= 20) step = 5;
  else if (maxValue <= 50) step = 10;
  else if (maxValue <= 100) step = 25;
  else if (maxValue <= 250) step = 50;

  const top = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let tick = 0; tick <= top; tick += step) {
    ticks.push(tick);
  }
  return { domain: [0, top], ticks };
};

function TrendToolbar({
  groupBy,
  onGroupByChange,
  periodLabel,
  dateSelected,
  setDateSelected,
  prevDateSelected,
  setPrevDateSelected,
  onPeriodChange,
  onExport,
}) {
  return (
    <div className="TestUsageTrend__Toolbar">
      <div className="TestUsageTrend__GroupBy">
        {GROUP_BY_OPTIONS.map((option) => {
          const isActive = option.key === groupBy;
          return (
            <Button
              key={option.key}
              type="default"
              className={[
                "TestUsageTrend__GroupByButton",
                isActive ? "TestUsageTrend__GroupByButton--active" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => onGroupByChange(option.key)}
            >
              {option.label}
            </Button>
          );
        })}
      </div>

      <DatePicker
        handleSearchDate={onPeriodChange}
        value={dateSelected}
        onChange={setDateSelected}
        prevValue={prevDateSelected}
        setPrevValue={setPrevDateSelected}
        searchOnClear
        popoverPlacement="bottomRight"
        allowFutureDateSelect
      >
        <Button
          type="default"
          className="TestUsageTrend__Period"
          icon={<CalendarOutlined />}
        >
          <span>{dateSelected || periodLabel}</span>
          <DownOutlined className="TestUsageTrend__PeriodChevron" />
        </Button>
      </DatePicker>

      <StyledTooltip title="Export Data" placement="top">
        <Button
          type="default"
          className="TestUsageTrend__Export"
          icon={<UploadOutlined />}
          aria-label="Export usage trend"
          onClick={onExport}
        />
      </StyledTooltip>
    </div>
  );
}

function TrendChart({
  data = [],
  series = [],
}) {
  const { domain, ticks } = useMemo(
    () => getChartYDomain(data, series),
    [data, series],
  );

  return (
    <div className="TestUsageTrend__Chart">
      <p className="TestUsageTrend__ChartTitle">Credits used over time</p>
      <div className="TestUsageTrend__ChartCanvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 12, right: 20, left: 4, bottom: 40,
            }}
          >
            <CartesianGrid
              vertical
              horizontal={false}
              stroke={designTokens.colorBorderPrimary}
              strokeDasharray="3 4"
            />
            <XAxis
              dataKey="week"
              tick={{ fill: designTokens.colorContentTertiary, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: designTokens.colorBorderPrimary }}
              interval={0}
              label={{
                value: "Week",
                position: "insideBottom",
                offset: -14,
                style: {
                  fill: designTokens.colorContentTertiary,
                  fontSize: 12,
                },
              }}
            />
            <YAxis
              domain={domain}
              ticks={ticks}
              label={{
                value: "Credits Used",
                angle: -90,
                position: "insideLeft",
                offset: 8,
                style: {
                  fill: designTokens.colorContentTertiary,
                  fontSize: 12,
                },
              }}
              tick={{ fill: designTokens.colorContentTertiary, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={58}
            />
            <Tooltip
              contentStyle={{
                border: `1px solid ${designTokens.colorBorderPrimary}`,
                borderRadius: 8,
                backgroundColor: designTokens.colorBgPrimary,
              }}
              labelStyle={{ color: designTokens.colorContentBase }}
            />
            <Legend
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ paddingTop: 20 }}
              content={({ payload }) => (
                <ul className="TestUsageTrend__Legend">
                  {(payload || []).map((entry) => (
                    <li className="TestUsageTrend__LegendItem" key={entry.value}>
                      <span
                        className="TestUsageTrend__LegendSwatch"
                        style={{ backgroundColor: entry.color }}
                        aria-hidden
                      />
                      <span className="TestUsageTrend__LegendLabel">{entry.value}</span>
                    </li>
                  ))}
                </ul>
              )}
            />
            {series.map((item) => (
              <Line
                key={item.key}
                type="monotone"
                dataKey={item.key}
                name={item.label}
                stroke={SERIES_COLORS[item.colorToken]}
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: designTokens.colorSecondary,
                  stroke: designTokens.colorBgPrimary,
                  strokeWidth: 1,
                }}
                activeDot={{
                  r: 5,
                  fill: designTokens.colorSecondary,
                  stroke: designTokens.colorBgPrimary,
                  strokeWidth: 1,
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function TestUsageTrend({
  periodLabel = "Last 30 days",
  location = "",
  onExport,
  onPeriodChange,
}) {
  const defaultRange = useMemo(() => getDefaultUsageTrendDateRange(30), []);
  const csvLinkRef = useRef(null);
  const shouldDownloadRef = useRef(false);
  const [groupBy, setGroupBy] = useState("category");
  const [visiblePeriodLabel, setVisiblePeriodLabel] = useState(periodLabel);
  const [dateSelected, setDateSelected] = useState("");
  const [prevDateSelected, setPrevDateSelected] = useState("");
  const [dateRange, setDateRange] = useState(defaultRange);
  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [usageInsights, setUsageInsights] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [csvFilename, setCsvFilename] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadGraph = async () => {
      setGraphLoading(true);
      const response = await fetchCreditConsumptionGraph({
        groupBy,
        location,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        signal: controller.signal,
      });

      if (response.canceled) return;

      if (!response.success) {
        setGraphLoading(false);
        return;
      }

      const mapped = groupBy === "location"
        ? mapCreditConsumptionGraphByLocation(response)
        : mapCreditConsumptionGraphToTrend(response);
      setChartData(mapped.data);
      setChartSeries(mapped.series);
      setUsageInsights(mapCreditConsumptionInsights(response, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }));
      setGraphLoading(false);
    };

    loadGraph();

    return () => {
      controller.abort();
    };
  }, [groupBy, location, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (
      !shouldDownloadRef.current
      || !exportData.length
      || !exportHeaders.length
      || !csvFilename
      || !csvLinkRef.current
    ) {
      return;
    }

    csvLinkRef.current.link.click();
    toast.success("Data exported successfully!");
    shouldDownloadRef.current = false;
    setExportData([]);
    setExportHeaders([]);
    setCsvFilename("");

    if (onExport) {
      onExport();
    }
  }, [exportData, exportHeaders, csvFilename, onExport]);

  const isCategoryView = groupBy === "category";
  let panelTitle = "Test Usage Trend";
  if (isCategoryView) {
    panelTitle = location
      ? `Test Usage Trend By Category: ${location}`
      : "Test Usage Trend By Category";
  } else if (location) {
    panelTitle = `Test Usage Trend By Location: ${location}`;
  } else {
    panelTitle = "Test Usage Trend By Location";
  }

  const handlePeriodChange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      const monthRange = getOneMonthTrendRange();
      setDateRange(monthRange);
      setVisiblePeriodLabel(formatTrendPeriodLabel(monthRange.startDate, monthRange.endDate));
      if (onPeriodChange) {
        onPeriodChange(monthRange.startDate, monthRange.endDate);
      }
      return;
    }

    const resolvedEndDate = endDate || startDate;

    if (onPeriodChange) {
      onPeriodChange(startDate, resolvedEndDate);
    }

    if (startDate) {
      setDateRange({ startDate, endDate: resolvedEndDate });
    }
  };

  const handleExport = () => {
    if (graphLoading) return;

    if (!chartData.length) {
      toast.error("No usage trend data available to export");
      return;
    }

    const { headers, rows } = buildUsageTrendCsvExport(chartData, chartSeries);
    if (!headers.length || !rows.length) {
      toast.error("No usage trend data available to export");
      return;
    }

    shouldDownloadRef.current = true;
    setExportHeaders(headers);
    setExportData(rows);
    setCsvFilename(buildUsageTrendCsvFilename({
      groupBy,
      location,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }));
  };

  return (
    <>
      <TitledPanel
        className="TestUsageTrend"
        title={panelTitle}
        extra={(
          <TrendToolbar
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            periodLabel={visiblePeriodLabel}
            dateSelected={dateSelected}
            setDateSelected={setDateSelected}
            prevDateSelected={prevDateSelected}
            setPrevDateSelected={setPrevDateSelected}
            onPeriodChange={handlePeriodChange}
            onExport={handleExport}
          />
        )}
      >
        <div
          className={[
            "TestUsageTrend__Body",
            usageInsights.length ? "TestUsageTrend__Body--withInsights" : "",
          ].filter(Boolean).join(" ")}
        >
          {graphLoading ? (
            <div className="TestUsageTrend__Loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <TrendChart data={chartData} series={chartSeries} />
              <UsageInsightCards insights={usageInsights} />
            </>
          )}
        </div>
      </TitledPanel>
      <CSVLink
        ref={csvLinkRef}
        data={exportData}
        headers={exportHeaders}
        filename={csvFilename}
        className="TestUsageTrend__HiddenCsvLink"
      />
    </>
  );
}
